import React from "react";
import SeoHead from "@/components/brand/SeoHead";
import GlowingGridBackground from "@/components/brand/GlowingGridBackground";
import GlassCard from "@/components/brand/GlassCard";
import FadeUp from "@/components/brand/FadeUp";
import {
    FormField,
    FormSelect,
    FormTextarea,
    FormCheckboxGroup,
    HoneypotField,
    FormSubmitButton,
    FormErrorBanner,
    FormSuccessState,
} from "@/components/forms/FormControls";
import {
    useFormSubmit,
    isNonEmpty,
    isEmail,
    isPhone,
    minLength,
} from "@/components/forms/useFormSubmit";

const ORG_TYPES = [
    "Library",
    "School",
    "Youth organization",
    "Community center",
    "Local business",
    "Other",
];

const INTERESTS = [
    "Hosting space",
    "Student referrals",
    "Sponsorship",
    "Volunteer support",
    "Community collaboration",
];

const WAYS = [
    {
        title: "Co-hosting",
        body: "Provide a safe, welcoming space where the lab can meet.",
        color: "#EC4899",
        icon: "M3 9.5 L12 3 L21 9.5 V20 H3 Z M9 20v-6h6v6",
    },
    {
        title: "Student referrals",
        body: "Help connect interested students and families to the program.",
        color: "#8B5CF6",
        icon: "M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M3 21v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1 M14 21v-1a4 4 0 0 1 4-4h2a3 3 0 0 1 3 3v2",
    },
    {
        title: "Space partnerships",
        body: "Open a community room, library, or learning space for cohorts.",
        color: "#3B82F6",
        icon: "M4 6h16 M4 12h16 M4 18h10 M18 16l4 4M22 16l-4 4",
    },
    {
        title: "Sponsor connections",
        body: "Introduce local businesses who may want to sponsor a seat.",
        color: "#14B8A6",
        icon: "M12 21l-1.5-1.4C5.4 15 2 12 2 8.5 2 6 4 4 6.5 4c1.5 0 2.95.7 4 1.9 1.05-1.2 2.5-1.9 4-1.9C17 4 19 6 19 8.5 19 12 15.6 15 10.5 19.6 L12 21z",
    },
    {
        title: "Community support",
        body: "Share, amplify, or volunteer in pilot-appropriate ways.",
        color: "#FBBF24",
        icon: "M3 12h3 M18 12h3 M12 3v3 M12 18v3 M5.6 5.6l2 2 M16.4 16.4l2 2 M5.6 18.4l2-2 M16.4 7.6l2-2 M12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6",
    },
];

const Icon = ({ d, color }) => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
            d={d}
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const initialValues = {
    organizationName: "",
    contactName: "",
    roleTitle: "",
    email: "",
    phone: "",
    organizationType: "",
    partnershipInterest: [],
    message: "",
    hp_company_website: "",
};

const validate = (v) => {
    const e = {};
    if (!isNonEmpty(v.organizationName))
        e.organizationName = "This field is required.";
    if (!isNonEmpty(v.contactName)) e.contactName = "This field is required.";
    if (!isNonEmpty(v.roleTitle)) e.roleTitle = "This field is required.";
    if (!isEmail(v.email)) e.email = "Please enter a valid email address.";
    if (!isPhone(v.phone))
        e.phone =
            "Please enter a valid phone number (digits, spaces, +, -, ()).";
    if (!isNonEmpty(v.organizationType))
        e.organizationType = "Please choose an organization type.";
    if (!v.partnershipInterest || v.partnershipInterest.length === 0)
        e.partnershipInterest = "Please select at least one option.";
    if (!minLength(v.message, 10))
        e.message = "Please write at least 10 characters.";
    return e;
};

export default function Partner() {
    const form = useFormSubmit({
        formType: "partner",
        initialValues,
        validate,
    });

    return (
        <>
            <SeoHead
                title="Partner With Vibe for Success | Lee County Youth Digital Skills Program"
                description="Help bring safe, creative, AI-era workforce-readiness opportunities to Lee County students. Co-host space, refer students, or support the pilot."
                path="/partner"
            />

            {/* Hero */}
            <section
                className="vfs-section relative overflow-hidden"
                style={{ paddingTop: "clamp(2rem, 6vw, 4rem)" }}
                data-testid="partner-hero"
            >
                <GlowingGridBackground accent="#14B8A6" opacity={0.16} />
                <div className="vfs-container relative">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <span className="vfs-eyebrow">Partner With Us</span>
                            <h1 className="vfs-h1 mt-5" data-testid="partner-h1">
                                Partner with{" "}
                                <span className="vfs-grad-text">
                                    Vibe for Success.
                                </span>
                            </h1>
                            <p
                                className="mt-6 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Help bring safe, creative, AI-era
                                workforce-readiness opportunities to Lee County
                                students.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* Lead paragraph */}
            <section className="vfs-section" style={{ paddingTop: 0 }}>
                <div className="vfs-container">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <p
                                className="text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Vibe for Success is seeking aligned community
                                partners who can help support safe, creative
                                workforce-readiness opportunities for students.
                                Potential partners may include youth
                                organizations, libraries, schools, community
                                centers, local businesses, and organizations
                                interested in helping students build confidence
                                with modern digital tools.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* Ways to partner */}
            <section
                className="vfs-section relative overflow-hidden"
                data-testid="partner-ways"
            >
                <GlowingGridBackground accent="#8B5CF6" opacity={0.08} />
                <div className="vfs-container relative">
                    <FadeUp>
                        <h2 className="vfs-h2">Ways to partner</h2>
                    </FadeUp>
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {WAYS.map((w, i) => (
                            <FadeUp key={w.title} delay={i * 0.05}>
                                <GlassCard
                                    accentColor={w.color}
                                    className="p-7 h-full"
                                >
                                    <div
                                        className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-xl"
                                        style={{
                                            background: `${w.color}18`,
                                            border: `1px solid ${w.color}45`,
                                        }}
                                    >
                                        <Icon d={w.icon} color={w.color} />
                                    </div>
                                    <h3 className="vfs-h3 mb-2">{w.title}</h3>
                                    <p
                                        className="text-sm leading-relaxed"
                                        style={{
                                            color: "var(--text-secondary)",
                                        }}
                                    >
                                        {w.body}
                                    </p>
                                </GlassCard>
                            </FadeUp>
                        ))}
                    </div>
                    <FadeUp>
                        <p
                            className="mt-8 text-sm italic max-w-2xl"
                            style={{ color: "var(--text-secondary)" }}
                        >
                            We&apos;re intentionally starting small. Volunteer
                            or mentor roles will be added carefully as the
                            pilot grows.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* Form */}
            <section
                id="partner-form"
                className="vfs-section"
                style={{ paddingTop: 0, scrollMarginTop: "6rem" }}
                data-testid="partner-form-section"
            >
                <div className="vfs-container">
                    <FadeUp>
                        <GlassCard
                            className="p-6 sm:p-10 max-w-3xl mx-auto"
                            hoverLift={false}
                        >
                            {form.success ? (
                                <FormSuccessState
                                    heading="Thank you — we'd love to connect."
                                    body={"We'll follow up by email to talk through partnership next steps."}
                                    onReset={form.reset}
                                    testId="partner-success"
                                />
                            ) : (
                                <form
                                    onSubmit={form.handleSubmit}
                                    noValidate
                                    data-testid="partner-form"
                                >
                                    <h2 className="vfs-h2 mb-3">
                                        Partner inquiry
                                    </h2>
                                    <p
                                        className="text-sm mb-7"
                                        style={{
                                            color: "var(--text-secondary)",
                                        }}
                                    >
                                        Tell us a little about your
                                        organization and how you&apos;d like to
                                        partner.
                                    </p>
                                    <FormErrorBanner message={form.serverError} />
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <FormField
                                            id="organizationName"
                                            name="organizationName"
                                            label="Organization name"
                                            required
                                            autoComplete="organization"
                                            maxLength={200}
                                            value={form.values.organizationName}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.organizationName}
                                            testId="partner-org-name"
                                        />
                                        <FormField
                                            id="contactName"
                                            name="contactName"
                                            label="Contact name"
                                            required
                                            autoComplete="name"
                                            maxLength={100}
                                            value={form.values.contactName}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.contactName}
                                            testId="partner-contact-name"
                                        />
                                        <FormField
                                            id="roleTitle"
                                            name="roleTitle"
                                            label="Role / title"
                                            required
                                            maxLength={100}
                                            value={form.values.roleTitle}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.roleTitle}
                                            testId="partner-role"
                                        />
                                        <FormField
                                            id="email"
                                            name="email"
                                            type="email"
                                            label="Email"
                                            required
                                            autoComplete="email"
                                            value={form.values.email}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.email}
                                            testId="partner-email"
                                            inputMode="email"
                                        />
                                        <FormField
                                            id="phone"
                                            name="phone"
                                            type="tel"
                                            label="Phone"
                                            required
                                            autoComplete="tel"
                                            value={form.values.phone}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.phone}
                                            testId="partner-phone"
                                            inputMode="tel"
                                        />
                                        <FormSelect
                                            id="organizationType"
                                            name="organizationType"
                                            label="Organization type"
                                            required
                                            options={ORG_TYPES}
                                            value={form.values.organizationType}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.organizationType}
                                            testId="partner-org-type"
                                        />
                                    </div>

                                    <div className="mt-7">
                                        <FormCheckboxGroup
                                            name="partnershipInterest"
                                            legend="Partnership interest"
                                            required
                                            options={INTERESTS}
                                            value={
                                                form.values.partnershipInterest
                                            }
                                            onChange={form.handleChange}
                                            error={
                                                form.errors.partnershipInterest
                                            }
                                            testId="partner-interests"
                                        />
                                    </div>

                                    <div className="mt-7">
                                        <FormTextarea
                                            id="message"
                                            name="message"
                                            label="Message"
                                            required
                                            value={form.values.message}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.message}
                                            testId="partner-message"
                                        />
                                    </div>

                                    <HoneypotField
                                        value={form.values.hp_company_website}
                                        onChange={form.handleChange}
                                    />

                                    <div className="mt-8">
                                        <FormSubmitButton
                                            submitting={form.submitting}
                                            testId="partner-submit"
                                        >
                                            Send Partner Inquiry
                                        </FormSubmitButton>
                                    </div>
                                </form>
                            )}
                        </GlassCard>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
